import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import {
    PlusIcon,
    PencilSquareIcon,
    TrashIcon,
    XMarkIcon,
    PhotoIcon,
    CheckCircleIcon,
    MagnifyingGlassIcon,
    Squares2X2Icon,
    DevicePhoneMobileIcon,
    ComputerDesktopIcon
} from '@heroicons/react/24/outline';
import { API } from '../../config';

const AdminSections = () => {
    const [form, setForm] = useState({
        title: '',
        description: '',
        desktopBanner: null,
        mobileBanner: null,
    });
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedSubcategory, setSelectedSubcategory] = useState('');
    const [sections, setSections] = useState([]);
    const [editingSection, setEditingSection] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);

    // Previews
    const [desktopPreview, setDesktopPreview] = useState(null);
    const [mobilePreview, setMobilePreview] = useState(null);

    useEffect(() => {
        fetchCategories();
        fetchSubcategories();
        fetchSections();
        fetchProducts();
    }, []);

    // Cleanup previews
    useEffect(() => {
        return () => {
            if (desktopPreview) URL.revokeObjectURL(desktopPreview);
            if (mobilePreview) URL.revokeObjectURL(mobilePreview);
        };
    }, [desktopPreview, mobilePreview]);

    const fetchProducts = async () => {
        try {
            const response = await axios.get(`${API}/products`);
            setProducts(response.data);
        } catch (error) {
            setProducts([]);
        }
    };

    const fetchSections = async () => {
        try {
            const response = await axios.get(`${API}/sections`);
            setSections(response.data);
        } catch (error) {
            setSections([]);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get(`${API}/categories`);
            setCategories(response.data);
        } catch (error) {
            setCategories([]);
        }
    };

    const fetchSubcategories = async () => {
        try {
            const response = await axios.get(`${API}/subcategories`);
            setSubcategories(response.data);
        } catch (error) {
            setSubcategories([]);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            setForm((prev) => ({ ...prev, [type]: file }));
            const previewUrl = URL.createObjectURL(file);
            if (type === 'desktopBanner') setDesktopPreview(previewUrl);
            if (type === 'mobileBanner') setMobilePreview(previewUrl);
        }
    };

    const handleProductSelect = (product) => {
        setSelectedProducts((prev) => {
            if (prev.find((p) => p.id === product.id)) {
                return prev.filter((p) => p.id !== product.id);
            } else {
                return [...prev, product];
            }
        });
    };

    // Filter products based on selected category and subcategory
    const filteredProducts = products.filter((product) => {
        let match = true;
        if (selectedCategory) match = match && product.category_id === selectedCategory;
        if (selectedSubcategory) match = match && product.subcategory_id === selectedSubcategory;
        return match;
    });

    const handleEdit = (section) => {
        setEditingSection(section);
        setForm({
            title: section.title,
            description: section.description,
            desktopBanner: null,
            mobileBanner: null,
        });
        setDesktopPreview(section.desktop_banner_url);
        setMobilePreview(section.mobile_banner_url);
        setSelectedProducts(
            products.filter(p => section.product_ids.includes(p.id))
        );
        setShowAddForm(true);
    };

    const resetForm = () => {
        setForm({ title: '', description: '', desktopBanner: null, mobileBanner: null });
        setSelectedProducts([]);
        setEditingSection(null);
        setDesktopPreview(null);
        setMobilePreview(null);
        setShowAddForm(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const formData = new FormData();
            formData.append('title', form.title);
            formData.append('description', form.description);
            if (form.desktopBanner) formData.append('desktop_banner', form.desktopBanner);
            if (form.mobileBanner) formData.append('mobile_banner', form.mobileBanner);
            formData.append('product_ids', JSON.stringify(selectedProducts.map(p => p.id)));

            if (editingSection) {
                await axios.put(`${API}/sections/${editingSection.id}`, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data',
                    },
                });
                toast.success('Section updated successfully!');
            } else {
                await axios.post(`${API}/sections`, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data',
                    },
                });
                toast.success('Section added successfully!');
            }
            resetForm();
            fetchSections();
        } catch (error) {
            toast.error(editingSection ? 'Failed to update section' : 'Failed to add section');
        } finally {
            setLoading(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl shadow-sm p-8"
        >
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Sections</h2>
                    <p className="text-gray-500 text-sm mt-1">Manage featured collections and product sections</p>
                </div>
                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => { resetForm(); setShowAddForm(true); }}
                    className="bg-gray-900 text-white px-5 py-2.5 rounded-xl hover:bg-black transition-all shadow-sm hover:shadow-md flex items-center font-medium text-sm"
                >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Add Section
                </motion.button>
            </div>

            <AnimatePresence>
                {showAddForm && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden mb-8"
                    >
                        <div className="bg-gray-50 rounded-3xl p-8 border border-gray-200">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-lg font-bold text-gray-900 flex items-center">
                                    {editingSection ? <PencilSquareIcon className="h-5 w-5 mr-2 text-gray-700" /> : <PlusIcon className="h-5 w-5 mr-2 text-gray-700" />}
                                    {editingSection ? 'Edit Section' : 'Add New Section'}
                                </h3>
                                <button
                                    onClick={resetForm}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <XMarkIcon className="h-5 w-5" />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-8">
                                <div className="grid md:grid-cols-2 gap-8">
                                    <div className="space-y-6">
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Title</label>
                                            <input
                                                type="text"
                                                name="title"
                                                value={form.title}
                                                onChange={handleInputChange}
                                                required
                                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                                placeholder="e.g., Summer Collection"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Description</label>
                                            <textarea
                                                name="description"
                                                value={form.description}
                                                onChange={handleInputChange}
                                                required
                                                rows="4"
                                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                                placeholder="Describe this section..."
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-6">
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide flex items-center">
                                                <ComputerDesktopIcon className="h-4 w-4 mr-1.5" />
                                                Desktop Banner
                                            </label>
                                            <div className="relative group cursor-pointer">
                                                <div className={`border-2 border-dashed rounded-xl p-4 text-center transition-all aspect-[3/4] h-auto flex flex-col justify-center items-center ${desktopPreview ? 'border-gray-300 bg-white' : 'border-gray-300 hover:border-gray-400 bg-white hover:bg-gray-50'
                                                    }`}>
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={(e) => handleFileChange(e, 'desktopBanner')}
                                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                                    />
                                                    {desktopPreview ? (
                                                        <img src={desktopPreview} alt="Desktop Preview" className="h-full w-full object-cover rounded-lg" />
                                                    ) : (
                                                        <div className="text-gray-400 flex flex-col items-center">
                                                            <PhotoIcon className="h-8 w-8 mb-1" />
                                                            <span className="text-xs">Upload Desktop Image</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide flex items-center">
                                                <DevicePhoneMobileIcon className="h-4 w-4 mr-1.5" />
                                                Mobile Banner
                                            </label>
                                            <div className="relative group cursor-pointer">
                                                <div className={`border-2 border-dashed rounded-xl p-4 text-center transition-all h-32 flex flex-col justify-center items-center ${mobilePreview ? 'border-gray-300 bg-white' : 'border-gray-300 hover:border-gray-400 bg-white hover:bg-gray-50'
                                                    }`}>
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={(e) => handleFileChange(e, 'mobileBanner')}
                                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                                    />
                                                    {mobilePreview ? (
                                                        <img src={mobilePreview} alt="Mobile Preview" className="h-full w-full object-cover rounded-lg" />
                                                    ) : (
                                                        <div className="text-gray-400 flex flex-col items-center">
                                                            <PhotoIcon className="h-8 w-8 mb-1" />
                                                            <span className="text-xs">Upload Mobile Image</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Product Selection Panel */}
                                <div className="bg-white p-6 rounded-2xl border border-gray-200">
                                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-4 flex items-center">
                                        <Squares2X2Icon className="h-4 w-4 mr-2" />
                                        Select Products
                                    </h3>
                                    <div className="flex gap-4 mb-6">
                                        <div className="flex-1">
                                            <select
                                                value={selectedCategory}
                                                onChange={e => { setSelectedCategory(e.target.value); setSelectedSubcategory(''); }}
                                                className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-1 focus:ring-gray-900 focus:border-gray-900 text-sm"
                                            >
                                                <option value="">All Categories</option>
                                                {categories.map(cat => (
                                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="flex-1">
                                            <select
                                                value={selectedSubcategory}
                                                onChange={e => setSelectedSubcategory(e.target.value)}
                                                className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-1 focus:ring-gray-900 focus:border-gray-900 text-sm"
                                                disabled={!selectedCategory}
                                            >
                                                <option value="">All Subcategories</option>
                                                {subcategories.filter(sub => sub.category_id === selectedCategory).map(sub => (
                                                    <option key={sub.id} value={sub.id}>{sub.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    {(selectedCategory && selectedSubcategory) ? (
                                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-80 overflow-y-auto pr-2 custom-scrollbar">
                                            {filteredProducts.map((product) => {
                                                const isSelected = selectedProducts.find(p => p.id === product.id);
                                                return (
                                                    <div
                                                        key={product.id}
                                                        onClick={() => handleProductSelect(product)}
                                                        className={`cursor-pointer border p-3 rounded-xl flex items-center justify-between transition-all ${isSelected
                                                            ? 'border-gray-900 bg-gray-50 ring-1 ring-gray-900'
                                                            : 'border-gray-100 hover:border-gray-300 hover:shadow-sm'
                                                            }`}
                                                    >
                                                        <div className="flex items-center overflow-hidden">
                                                            <img src={product.images?.[0] || ''} alt={product.title} className="h-10 w-10 object-cover rounded-lg mr-3 bg-gray-100 flex-shrink-0" />
                                                            <div className="min-w-0">
                                                                <div className="font-medium text-gray-900 text-sm truncate">{product.title}</div>
                                                                <div className="text-xs text-gray-500">₹{product.price}</div>
                                                            </div>
                                                        </div>
                                                        <div className={`w-5 h-5 rounded-full border flex-shrink-0 flex items-center justify-center ml-2 ${isSelected ? 'bg-gray-900 border-gray-900' : 'border-gray-300'
                                                            }`}>
                                                            {isSelected && <CheckCircleIcon className="w-4 h-4 text-white" />}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="text-gray-400 text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200 text-sm">
                                            <MagnifyingGlassIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                                            Select a category and subcategory to browse products
                                        </div>
                                    )}

                                    {selectedProducts.length > 0 && (
                                        <div className="mt-6 pt-6 border-t border-gray-100">
                                            <h4 className="font-semibold text-xs text-gray-500 uppercase tracking-wide mb-3">Selected Products ({selectedProducts.length})</h4>
                                            <div className="flex flex-wrap gap-2">
                                                {selectedProducts.map((product) => (
                                                    <span key={product.id} className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-xs font-medium flex items-center border border-gray-200">
                                                        {product.title}
                                                        <button type="button" onClick={(e) => { e.stopPropagation(); handleProductSelect(product); }} className="ml-1.5 text-gray-400 hover:text-red-500">
                                                            <XMarkIcon className="w-3 h-3" />
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="flex space-x-3 pt-4 border-t border-gray-200">
                                    <motion.button
                                        whileHover={{ scale: 1.01 }}
                                        whileTap={{ scale: 0.99 }}
                                        type="submit"
                                        disabled={loading}
                                        className="bg-gray-900 text-white px-6 py-2.5 rounded-xl hover:bg-black transition-all shadow-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex-1 text-sm"
                                    >
                                        {loading ? 'Saving...' : (editingSection ? 'Update Section' : 'Add Section')}
                                    </motion.button>
                                    <motion.button
                                        whileHover={{ scale: 1.01 }}
                                        whileTap={{ scale: 0.99 }}
                                        type="button"
                                        onClick={resetForm}
                                        className="bg-white text-gray-700 border border-gray-300 px-6 py-2.5 rounded-xl hover:bg-gray-50 transition-all font-medium text-sm"
                                    >
                                        Cancel
                                    </motion.button>
                                </div>
                            </form>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <AnimatePresence>
                    {sections.length === 0 ? (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="col-span-full text-center py-16 bg-gray-50 rounded-3xl border border-dashed border-gray-200"
                        >
                            <Squares2X2Icon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-900 font-medium">No sections found</p>
                            <p className="text-sm text-gray-500 mt-1">Create sections to organize your products</p>
                        </motion.div>
                    ) : (
                        sections.map((section, index) => (
                            <motion.div
                                key={section.id}
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: index * 0.1 }}
                                className="group bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all"
                            >
                                <div className="relative aspect-[3/4] h-auto bg-gray-100">
                                    {section.desktop_banner_url ? (
                                        <img src={section.desktop_banner_url} alt="Desktop Banner" className="h-full w-full object-cover" />
                                    ) : (
                                        <div className="h-full w-full flex items-center justify-center text-gray-400 bg-gray-50">
                                            <PhotoIcon className="h-10 w-10 opacity-50" />
                                        </div>
                                    )}

                                    {/* Overlay Actions */}
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                                        <div className="flex space-x-2 transform translate-y-4 group-hover:translate-y-0 transition-transform">
                                            <motion.button
                                                whileHover={{ scale: 1.1 }}
                                                whileTap={{ scale: 0.9 }}
                                                onClick={() => handleEdit(section)}
                                                className="bg-white text-gray-900 p-2.5 rounded-full shadow-lg hover:bg-gray-50"
                                                title="Edit"
                                            >
                                                <PencilSquareIcon className="h-5 w-5" />
                                            </motion.button>
                                            <motion.button
                                                whileHover={{ scale: 1.1 }}
                                                whileTap={{ scale: 0.9 }}
                                                onClick={async () => {
                                                    if (window.confirm('Delete this section?')) {
                                                        const token = localStorage.getItem('token');
                                                        await axios.delete(`${API}/sections/${section.id}`, {
                                                            headers: { Authorization: `Bearer ${token}` }
                                                        });
                                                        toast.success('Section deleted!');
                                                        fetchSections();
                                                    }
                                                }}
                                                className="bg-white text-red-600 p-2.5 rounded-full shadow-lg hover:bg-red-50"
                                                title="Delete"
                                            >
                                                <TrashIcon className="h-5 w-5" />
                                            </motion.button>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-6">
                                    <h4 className="font-bold text-lg text-gray-900 mb-2 line-clamp-1">{section.title}</h4>
                                    <p className="text-gray-500 text-sm mb-4 line-clamp-2 h-10">{section.description}</p>
                                    <div className="flex items-center justify-between pt-4 border-t border-gray-50">
                                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Products</span>
                                        <span className="bg-gray-100 text-gray-900 px-2.5 py-1 rounded-lg text-xs font-bold border border-gray-200">
                                            {section.product_ids.length}
                                        </span>
                                    </div>
                                </div>
                            </motion.div>
                        ))
                    )}
                </AnimatePresence>
            </div>
        </motion.div>
    );
};

export default AdminSections;
